/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { type ReactNode, createContext, useCallback } from 'react';
import noop from 'lodash/noop';
import { NAVIGATION_ITEM } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import {
	CUSTOM_ITEM,
	FIXED_ITEM,
} from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { LEVEL_ONE } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { FixItemTypeId, RecentItemType, TimeRangeType } from '../../constants.tsx';

const COMMON_PAYLOAD = {
	action: 'clicked',
	actionSubject: NAVIGATION_ITEM,
	actionSubjectId: 'recentNavigationMenuItem',
};

type AnalyticContextType = {
	fireUIAnalyticForFixItem({ itemId }: { itemId: FixItemTypeId }): void;
	fireUIAnalyticForRecentItems({
		itemId,
		itemType,
		timeRange,
	}: {
		itemId: string;
		itemType: RecentItemType;
		timeRange: TimeRangeType;
	}): void;
};

export const AnalyticContext: React.Context<AnalyticContextType> =
	createContext<AnalyticContextType>({
		fireUIAnalyticForFixItem: noop,
		fireUIAnalyticForRecentItems: noop,
	});

export const AnalyticContextProvider = ({
	children,
}: {
	children: ReactNode;
}): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireUIAnalyticForFixItem = useCallback<AnalyticContextType['fireUIAnalyticForFixItem']>(
		({ itemId }) => {
			fireUIAnalytics(createAnalyticsEvent({}), {
				...COMMON_PAYLOAD,
				attributes: {
					level: LEVEL_ONE,
					componentType: FIXED_ITEM,
					itemId,
				},
			});
		},
		[createAnalyticsEvent],
	);

	const fireUIAnalyticForRecentItems = useCallback<
		AnalyticContextType['fireUIAnalyticForRecentItems']
	>(
		({ itemId, itemType, timeRange }) => {
			fireUIAnalytics(createAnalyticsEvent({}), {
				...COMMON_PAYLOAD,
				attributes: {
					level: LEVEL_ONE,
					componentType: CUSTOM_ITEM,
					itemType: itemType.toLowerCase(),
					itemId,
					section: timeRange,
				},
			});
		},
		[createAnalyticsEvent],
	);

	return (
		<AnalyticContext.Provider
			value={{
				fireUIAnalyticForFixItem,
				fireUIAnalyticForRecentItems,
			}}
		>
			{children}
		</AnalyticContext.Provider>
	);
};
